import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { isQuizNotFoundError } from '~api/isQuizNotFoundError';
import { getQuizById, loadQuiz } from '~api/quizApi';
import { normalize } from 'normalizr';

import { getActivityByInstanceIdOptions } from '~teacher/hook/activity';
import {
  Quiz,
  QuizAnswer,
  QuizQuestion,
  QuizSchema,
} from '~teacher/models/quizzes';

export type QuizResponse = {
  quiz: Quiz;
  quizQuestion: Record<string, QuizQuestion>;
  quizAnswer: Record<string, QuizAnswer>;
};

interface GetQuizParams {
  quizId: number;
  quizUrl: ({ quizId }?: { quizId?: number }) => string;
}

export async function getQuiz({
  quizId,
  quizUrl,
}: GetQuizParams): Promise<QuizResponse> {
  const data = await getQuizById({ quizId: quizId, quizUrl: quizUrl });
  const result = normalize<
    any,
    {
      quiz: Record<string, Quiz>;
      quizQuestion: Record<string, QuizQuestion>;
      quizAnswer: Record<string, QuizAnswer>;
    },
    any
  >(data, QuizSchema);
  const {
    entities: { quiz, quizQuestion = {}, quizAnswer = {} },
  } = result;

  return {
    quiz: Object.values<Quiz>(quiz)[0],
    quizQuestion,
    quizAnswer,
  };
}

export function useQuiz(
  quizId: number,
  quizUrl: ({ quizId }?: { quizId?: number }) => string
) {
  return useQuery({
    queryKey: quizKeyFactory.byId(quizId),
    queryFn: () => getQuiz({ quizId: quizId, quizUrl: quizUrl }),
    enabled: !!quizId,
    // A quiz_not_found (deleted/inaccessible library) won't recover, so surface
    // it immediately instead of burning the default retries.
    retry: (failureCount, error) =>
      !isQuizNotFoundError(error) && failureCount <= 3,
  });
}

interface UseQuizBySocNumberParams {
  socNumber: string;
  quizUrl: ({ quizId }?: { quizId?: number }) => string;
}

export function useQuizBySocNumber({
  socNumber,
  quizUrl,
}: UseQuizBySocNumberParams) {
  const queryClient = useQueryClient();
  return useQuery(
    quizBySocNumberQueryOptions({ socNumber, quizUrl, queryClient })
  );
}

export function quizBySocNumberQueryOptions({
  socNumber,
  quizUrl,
  queryClient,
}: UseQuizBySocNumberParams & { queryClient: QueryClient }) {
  return {
    queryKey: quizKeyFactory.bySocNumber(socNumber),
    queryFn: async () => {
      const data = await loadQuiz({ socNumber: socNumber, quizUrl: quizUrl });
      // Normalize the data to match the shape expected by byId cache
      const result = normalize<
        any,
        {
          quiz: Record<string, Quiz>;
          quizQuestion: Record<string, QuizQuestion>;
          quizAnswer: Record<string, QuizAnswer>;
        },
        any
      >(data, QuizSchema);
      const {
        entities: { quiz, quizQuestion = {}, quizAnswer = {} },
      } = result;

      const quizResponse = {
        quiz: Object.values<Quiz>(quiz)[0],
        quizQuestion,
        quizAnswer,
      };

      queryClient.setQueryData(
        quizKeyFactory.byId(quizResponse.quiz.id),
        quizResponse
      );
      return quizResponse;
    },
    enabled: !!socNumber,
  };
}

export function useQuizByActivityId({
  activityInstanceId,
  quizUrl,
}: {
  activityInstanceId: number;
  quizUrl: ({ quizId }?: { quizId?: number }) => string;
}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: quizKeyFactory.byActivityId(activityInstanceId),
    queryFn: async () => {
      const { activity } = await queryClient.fetchQuery(
        getActivityByInstanceIdOptions({
          activityInstanceId: activityInstanceId,
        })
      );
      const quiz = await getQuiz({ quizId: activity.quizId, quizUrl });
      queryClient.setQueryData(quizKeyFactory.byId(activity.quizId), quiz);
      return quiz;
    },
  });
}

const quizKeyFactory = {
  all: ['quiz'] as const,
  byId: (quizId: number) =>
    [...quizKeyFactory.all, 'by-quiz-id', quizId] as const,
  byActivityId: (activityId: number) =>
    [...quizKeyFactory.all, 'by-activity-id', activityId] as const,
  bySocNumber: (socNumber: string) =>
    [...quizKeyFactory.all, 'by-soc-number', socNumber] as const,
};
